import * as React from 'react';
import { ReactNode } from 'react';
import { ValidationResult } from '../../../../util/validation/interfaces';
import { ApiResponse, ReferencedWorkDTO, SolrDocument, SuggestApi } from '../../../../api/core';
import { Suggester } from './Suggester';
import { renderSelectedTemplate } from './utils';
import { ParsedMathJax } from '../../../components/ParsedMathJax';

interface Props {
  value: SelectedWork[];
  onChange?: (values: SelectedWork[]) => void;
  multiple: boolean;
  disabled?: boolean;
  required?: boolean;
  validation?: ValidationResult;
  inputId?: string;
  autoFocus?: boolean;
  border?: 'border-noround' | 'border-noround-right';
  tooltip?: string;
  tooltipOptions?: any;
  className?: string;
}

export interface SelectedWork {
  id: number;
  title: string;
  subtitle: string;
  persons: string[];
  issued: number;
  pubtype: string;
}

interface SolrWork extends SolrDocument {
  id: string;
  main_title: string;
  subseries_title: string;
  person_name: string[];
  f_issued: number;
  f_publication_type: string;
}

export const referencedWorkToSelectedWork = (referenced: ReferencedWorkDTO): SelectedWork => {
  return {
    id: referenced.id,
    title: referenced.title,
    subtitle: referenced.subseriesTitle,
    persons: [],
    issued: undefined,
    pubtype: referenced.pubtype,
  };
};

export const selectedWorkToReferencedWork = (selected: SelectedWork): ReferencedWorkDTO => {
  return {
    id: selected.id,
    title: selected.title,
    subseriesTitle: selected.subtitle,
    pubtype: selected.pubtype,
  };
};

export function WorkSuggester(props: Props): ReactNode {
  const solrToSelectedWork = (solr: SolrWork): SelectedWork => {
    return {
      id: +solr.id,
      title: solr.main_title,
      subtitle: solr.subseries_title,
      persons: solr.person_name,
      issued: solr.f_issued,
      pubtype: solr.f_publication_type,
    };
  };

  const request = (params: { q: string }): Promise<ApiResponse<SolrDocument[]>> =>
    new SuggestApi().workSuggestRaw(params);

  const selectedItemTemplate = (item: ReferencedWorkDTO): ReactNode => {
    const content = <span>
        <ParsedMathJax content={item.title} inline={true} /> ({item.id})
      </span>;
    return renderSelectedTemplate(content, [], content);
  };

  const itemTemplate = (item: SelectedWork): ReactNode => {
    return (
      <div className={'w-full flex flex-column white-space-normal gap-1'}>
        <span className={'w-full'}><strong><ParsedMathJax content={item.title} /></strong></span>
        {item.subtitle && <span className={'w-full'}><ParsedMathJax content={item.subtitle} /></span>}
        {<span>({item.id})</span>}
        {item.issued && <div className={'w-full'}>{item.issued}</div>}
        {item.persons && item.persons.length > 0 &&
          <div className={'w-full'}>{item.persons.slice(0, 10).join('; ')}</div>}
      </div>
    );
  };

  return (
    <Suggester<SelectedWork, SelectedWork>
      itemTemplate={itemTemplate}
      autoFocus={props.autoFocus}
      selectedItemTemplate={selectedItemTemplate}
      load={request}
      map={solrToSelectedWork}
      inputId={props.inputId}
      value={props.value}
      disabled={props.disabled}
      validation={props.validation}
      required={props.required}
      onChange={props.onChange}
      border={props.border}
      tooltip={props.tooltip}
      tooltipOptions={props.tooltipOptions}
      className={props.className}
      multiple={props.multiple}/>
  );
}
